import { useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { FaRegCalendar } from "react-icons/fa";
import dayjs from "dayjs";
import cn from "mcn";

import { Button } from "../../../ui";
import { CloseIcon } from "../../../icons";
import { CheckIcon } from "../../../icons/CheckIcon";
import { ModalActionsRow } from "../../../components/ModalActionsRow";
import ClickAwayListener from "../../../components/ClickAwayListener";
import {
  classifyExpirationChoice,
  presetExpirationDates,
  type ExpirationChoice,
} from "../../../../data/alerts/expiration";

const formatDate = (iso: string) => dayjs(iso).format("DD-MMM-YY").toUpperCase();

export const ExpirationPicker = ({
  expiredAt,
  autoExpiredAt,
  disabled,
  onChange,
}: {
  /** Currently stored `expired_at`, or null/undefined if not yet resolvable (e.g. ambiguous period). */
  expiredAt: string | null | undefined;
  /** The period/contract-derived expiration that "None" resolves to, or null when it can't be previewed yet. */
  autoExpiredAt: string | null;
  disabled?: boolean;
  onChange: (expiredAt: string | null) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [pendingChoice, setPendingChoice] = useState<ExpirationChoice>("none");
  const [pendingCustomDate, setPendingCustomDate] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const customDateInputRef = useRef<HTMLInputElement>(null);

  const now = new Date();
  const currentChoice = classifyExpirationChoice(expiredAt, autoExpiredAt, now);
  const displayLabel = currentChoice === "none" ? "None" : expiredAt ? formatDate(expiredAt) : "None";

  const { today, oneWeek, oneMonth } = presetExpirationDates(now);
  const todayStr = dayjs(now).format("YYYY-MM-DD");

  const handleOpen = () => {
    if (disabled || !triggerRef.current) return;
    setAnchorRect(triggerRef.current.getBoundingClientRect());
    setPendingChoice(currentChoice);
    setPendingCustomDate(currentChoice === "custom" && expiredAt ? dayjs(expiredAt).format("YYYY-MM-DD") : todayStr);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setAnchorRect(null);
  };

  const handleConfirm = () => {
    switch (pendingChoice) {
      case "none":
        onChange(autoExpiredAt ?? null);
        break;
      case "today":
        onChange(today);
        break;
      case "1w":
        onChange(oneWeek);
        break;
      case "1m":
        onChange(oneMonth);
        break;
      case "custom":
        onChange(pendingCustomDate ? dayjs(pendingCustomDate).endOf("day").toISOString() : null);
        break;
    }
    handleClose();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid="expiration-picker"
        disabled={disabled}
        onClick={handleOpen}
        className={cn(
          "flex items-center justify-between gap-1 px-2 border rounded-lg text-sm box-border shrink-0 cursor-pointer",
          "bg-[var(--color-surface-1)] border-[var(--outline-default)]",
          "hover:bg-[var(--color-surface-2)] hover:border-[var(--color-outline-variant)]",
          "focus:bg-[var(--color-surface-2)] focus:outline-[1.5px] focus:outline-offset-[-1.5px] focus:outline-[#1D3DD5] dark:focus:outline-[#778CD9]",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-surface-1)] disabled:hover:border-[var(--outline-default)]",
        )}
        style={{ height: "27px", width: "7.5rem" }}
      >
        <span className="truncate">{displayLabel}</span>
        <FaRegCalendar
          className="text-foreground-muted shrink-0"
          style={{ width: "calc(var(--spacing) * 4)", height: "calc(var(--spacing) * 4)" }}
        />
      </button>

      {isOpen &&
        anchorRect &&
        createPortal(
          <ClickAwayListener onClickAway={handleClose}>
            <div
              data-click-away-inside
              className="fixed z-[9999]"
              style={{
                top: anchorRect.bottom + 4,
                left: anchorRect.left,
              }}
            >
              <ExpirationPickerPopup
                pendingChoice={pendingChoice}
                onPendingChoiceChange={setPendingChoice}
                pendingCustomDate={pendingCustomDate}
                onPendingCustomDateChange={setPendingCustomDate}
                customDateInputRef={customDateInputRef}
                today={today}
                oneWeek={oneWeek}
                oneMonth={oneMonth}
                todayStr={todayStr}
                onCancel={handleClose}
                onConfirm={handleConfirm}
              />
            </div>
          </ClickAwayListener>,
          document.body,
        )}
    </>
  );
};

const ExpirationPickerPopup = ({
  pendingChoice,
  onPendingChoiceChange,
  pendingCustomDate,
  onPendingCustomDateChange,
  customDateInputRef,
  today,
  oneWeek,
  oneMonth,
  todayStr,
  onCancel,
  onConfirm,
}: {
  pendingChoice: ExpirationChoice;
  onPendingChoiceChange: (choice: ExpirationChoice) => void;
  pendingCustomDate: string;
  onPendingCustomDateChange: (date: string) => void;
  customDateInputRef: RefObject<HTMLInputElement>;
  today: string;
  oneWeek: string;
  oneMonth: string;
  todayStr: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <div
      data-testid="expiration-picker-popup"
      className="p-4 bg-card shadow-card rounded-lg flex flex-col gap-2 border border-outline w-[213px]"
    >
      <div className="flex justify-between items-center mb-1">
        <p className="text-base font-medium text-foreground">Set expiration date to</p>
        <CloseIcon onClick={onCancel} className="shrink-0" />
      </div>

      <div className="flex flex-col gap-1">
        <OptionRow
          testId="expiration-option-none"
          primaryLabel="None"
          secondaryLabel="Won't expire"
          selected={pendingChoice === "none"}
          onClick={() => onPendingChoiceChange("none")}
        />
        <OptionRow
          testId="expiration-option-today"
          primaryLabel="Today"
          secondaryLabel={formatDate(today)}
          selected={pendingChoice === "today"}
          onClick={() => onPendingChoiceChange("today")}
        />
        <OptionRow
          testId="expiration-option-1w"
          primaryLabel="1 week"
          secondaryLabel={formatDate(oneWeek)}
          selected={pendingChoice === "1w"}
          onClick={() => onPendingChoiceChange("1w")}
        />
        <OptionRow
          testId="expiration-option-1m"
          primaryLabel="1 month"
          secondaryLabel={formatDate(oneMonth)}
          selected={pendingChoice === "1m"}
          onClick={() => onPendingChoiceChange("1m")}
        />
      </div>

      {/* Left inset (px-2) and trailing checkmark slot match OptionRow so the date field aligns
          with the secondary labels above it. Clicking the row (label / padding / check) selects
          Custom without opening the calendar; only the date control opens the picker. */}
      <div
        onClick={() => onPendingChoiceChange("custom")}
        className="flex items-center gap-3 px-2 py-1 rounded-md mt-1 hover:bg-neutral-tonal cursor-pointer"
      >
        <p className="text-sm font-medium text-foreground shrink-0">Custom:</p>
        <div className="relative flex-1" style={{ height: "27px" }}>
          {/* input type="date" doesn't allow custom formatting, so a fake text input is layered on top (same trick as the Snapshot date picker). */}
          <input
            ref={customDateInputRef}
            data-testid="expiration-custom-date"
            className="border-outline border rounded-sm p-1 w-full absolute z-[1] opacity-0 invisible pointer-events-none"
            type="date"
            min={todayStr}
            onChange={(e) => {
              onPendingChoiceChange("custom");
              onPendingCustomDateChange(e.target.value);
            }}
            value={pendingCustomDate}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPendingChoiceChange("custom");
              customDateInputRef.current?.showPicker();
            }}
            className={cn(
              "w-full flex items-center justify-between gap-1 px-2 border rounded-lg text-sm box-border cursor-pointer",
              "bg-[var(--color-surface-1)]",
              "hover:bg-[var(--color-surface-2)]",
              "focus:bg-[var(--color-surface-2)] focus:outline-[1.5px] focus:outline-offset-[-1.5px] focus:outline-[#1D3DD5] dark:focus:outline-[#778CD9]",
              pendingChoice === "custom"
                ? "border-primary hover:border-primary"
                : "border-[var(--outline-default)] hover:border-[var(--color-outline-variant)]",
            )}
            style={{ height: "27px" }}
          >
            <span className="truncate text-sm text-foreground-muted">
              {pendingCustomDate ? formatDate(pendingCustomDate) : "Select date"}
            </span>
            <FaRegCalendar
              className="text-foreground-muted shrink-0"
              style={{ width: "calc(var(--spacing) * 4)", height: "calc(var(--spacing) * 4)" }}
            />
          </button>
        </div>
        <CheckIcon
          className={cn("w-4 h-4 shrink-0 text-[var(--primary-default)]", pendingChoice !== "custom" && "invisible")}
        />
      </div>

      <ModalActionsRow className="mt-3">
        <Button onClick={onCancel} buttonType="cancel" data-testid="expiration-cancel">
          Cancel
        </Button>
        <Button onClick={onConfirm} buttonType="save" data-testid="expiration-confirm">
          Set date
        </Button>
      </ModalActionsRow>
    </div>
  );
};

const OptionRow = ({
  primaryLabel,
  secondaryLabel,
  selected,
  onClick,
  testId,
}: {
  primaryLabel: string;
  secondaryLabel: string;
  selected: boolean;
  onClick: () => void;
  testId: string;
}) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="flex items-center gap-3 px-2 py-1 rounded-md text-left hover:bg-neutral-tonal"
  >
    <span className="flex-1 flex items-center justify-between gap-3 min-w-0">
      <span className="text-sm font-medium text-foreground">{primaryLabel}</span>
      <span data-testid={`${testId}-date`} className="text-sm text-right text-foreground-muted">
        {secondaryLabel}
      </span>
    </span>
    {/* Reserve the checkmark's space even when hidden, so unselected rows still align with selected ones. */}
    <CheckIcon className={cn("w-4 h-4 shrink-0 text-[var(--primary-default)]", !selected && "invisible")} />
  </button>
);
